import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { HelperService } from './helper.service';
import { map, tap, share, finalize } from 'rxjs/operators';
import { BatchVote, MultiVersionVote, VersionVote } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class VoteService {
  private cache: any = {};
  private cachedObservable: any = {};
  private votedItem: any = null;

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly helperService: HelperService
  ) {}

  /**
   * Get votes by section
   * @param projectId
   * @param sectionId
   */
  getVotesBySection(projectId: string, sectionId: string, cacheRequest?: boolean): Observable<any> {
    return this.getVotes(projectId, sectionId, null, null, cacheRequest);
  }

  /**
   * Get votes by section and subsection
   * @param projectId
   * @param sectionId
   * @param subsectionId
   */
  getVotesBySectionAndSubsection(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    cacheRequest?: boolean
  ): Observable<any> {
    return this.getVotes(projectId, sectionId, subsectionId, null, cacheRequest);
  }

  /**
   * Get votes by section, subsection and typeId
   * @param projectId
   * @param sectionId
   * @param subsectionId
   * @param typeId
   */
  getVotesBySectionAndSubsectionAndTypeId(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    typeId: string,
    cacheRequest?: boolean
  ): Observable<any> {
    return this.getVotes(projectId, sectionId, subsectionId, typeId, cacheRequest);
  }

  sleep(milliSecond: any) {
    return new Promise((resolve) => setTimeout(resolve, milliSecond));
  }

  async delay(milliSecond: any) {
    await this.sleep(milliSecond);
    for (let i = 0; i < 5; i++) {
      if (i === 3) await this.sleep(milliSecond);
    }
  }

  private getVotes(
    projectId: string,
    sectionId: string,
    subsectionId: string = null,
    typeId: string = null,
    cacheRequest?: boolean
  ): Observable<any> {
    let observable: Observable<any>;
    const parameters = _.compact([projectId, sectionId, subsectionId, typeId]).join('/');
    const endpoint = `/api/votes/${parameters}`;
    if (cacheRequest) {
      if (this.cache[parameters]) {
        observable = of(this.cache[parameters]);
      } else if (this.cachedObservable[parameters]) {
        observable = this.cachedObservable[parameters];
      } else {
        this.cachedObservable[parameters] = this.apiService.httpGet(endpoint).pipe(
          tap((res) => (this.cache[parameters] = res)),
          share(),
          map((data: any) => {
            if (data && Array.isArray(data)) {
              data.map((vote: any) => {
                vote.avatarUrl = this.helperService.getAvatarNoImage();
              });
              data.reverse();
            }
            return data;
          }),
          finalize(() => {
            this.cachedObservable[parameters] = void 0;
            this.cache[parameters] = void 0;
          })
        );
        observable = this.cachedObservable[parameters];
      }
    } else {
      observable = this.apiService.httpGet(endpoint).pipe(
        map((data: any) => {
          if (data && Array.isArray(data)) {
            data.map((vote: any) => {
              vote.avatarUrl = this.helperService.getAvatarNoImage();
            });
            data.reverse();
          }
          return data;
        })
      );
    }
    return observable;
  }

  /**
   * Post a new vote
   * @param type
   * @param typeId
   * @param vote
   */
  postVote(projectId: string, sectionId: string, subsectionId: string, typeId: string, vote: any): Observable<any> {
    const body = {
      projectId: projectId,
      sectionId: sectionId,
      subSectionId: subsectionId,
      typeId: typeId,
      username: this.authService.getAuthUserName(),
      vote: vote,
    };

    return this.apiService.httpPost(`/api/votes/${projectId}/${sectionId}/${subsectionId}/${typeId}`, body);
  }

  /**
   * Delete vote
   * @param voteId
   */
  deleteVote(vote): Observable<any> {
    const authUser = this.authService.getAuthUserName();

    const endpoint = encodeURI(
      `/api/votes/${vote.projectId}/${vote.sectionId}/${vote.subSectionId}/${vote.typeId}/${vote.time}/${authUser}`
    );

    return this.apiService.httpDelete(endpoint);
  }

  /**
   * Save new asset vote for versioning system
   * @param vote vote information
   */
  sendVersionVote(vote: VersionVote): Observable<any> {
    const voteValue = (vote.value || 'NA').toUpperCase();
    const endpoint = `/api/votes/${vote.projectId}/${vote.sectionId}/${vote.subsectionId}/${vote.itemId}/${vote.versionNumber}?vote=${voteValue}`;
    return this.apiService.httpPost(endpoint, {});
  }

  sendVersionVotes(votes: MultiVersionVote) {
    return this.apiService.httpPost(
      `/api/votes/${votes.projectId}/${votes.sectionId}/${votes.subsectionId}`,
      votes.votes
    );
  }

  /***
   * Delete asset vote
   * @param vote
   */
  deleteVersionVote(vote: VersionVote): Observable<any> {
    const endpoint = `/api/votes/${vote.projectId}/${vote.sectionId}/${vote.subsectionId}/${vote.itemId}/${vote.versionNumber}`;
    return this.apiService.httpDelete(endpoint);
  }

  //stores temporary voted item
  storeComparedItem(value: any) {
    this.votedItem = value;
  }

  sendBatchVote(vote: BatchVote): Observable<any> {
    return this.apiService.httpPost(
      `/api/votes/batch/${vote.projectId}/${vote.sectionId}/${vote.subsectionId}/${vote.optionId}/${vote.version}`,
      vote.votes
    );
  }

  retrieveComparedItem(): any {
    const value = this.votedItem;
    this.votedItem = null;
    return value;
  }
}
